import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, mergeMap } from 'rxjs';
import { MessageService } from '@letly/ui-common';
import { ToastSeverity } from '@letly/models';
import { LandlordPropertyDetailService } from './landlord-property-detail.service';
import { landlordPropertyDetailActions } from './landlord-property-detail.actions';
import { switchMap } from 'rxjs/operators';
import { CreatePropertyService } from '../../create-property/data/create-property.service';
import { CreatePropertyModel } from '../../create-property/data/create-property.reducers';

@Injectable()
export class LandlordPropertyDetailEffects {
  private actions$ = inject(Actions);
  private propertyDetailService = inject(LandlordPropertyDetailService);
  private messageService = inject(MessageService);
  private createPropertyService = inject(CreatePropertyService);

  private toPropertyMutation(res: { success: boolean; data?: CreatePropertyModel; niceErrorMessage?: string }) {
    if (res.success) {
      return landlordPropertyDetailActions.onPropertyMutation({
        status: 'success',
        message: null,
        property: res.data
      });
    }
    this.messageService.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
    return landlordPropertyDetailActions.onPropertyMutation({
      status: 'error',
      message: res.niceErrorMessage,
      property: null
    });
  }

  loadDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(landlordPropertyDetailActions.detailsLoad),
      mergeMap((action) =>
        this.propertyDetailService.getPropertyDetail(action.id).pipe(
          map((res) => {
            if (res.success) {
              return landlordPropertyDetailActions.detailsLoaded({
                status: 'success',
                message: null,
                property: res.data
              });
            } else {
              return landlordPropertyDetailActions.detailsLoaded({
                status: 'error',
                message: res.niceErrorMessage,
                property: null
              });
            }
          })
        )
      )
    )
  );

  updatePropertyDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(landlordPropertyDetailActions.updatePropertyDetail),
      switchMap(({ id, data }) =>
        this.createPropertyService.updatePropertyDetail(id, data).pipe(
          map((res) => {
            if (res.success) {
              this.messageService.showSnackbar(
                $localize`:@@property.landlord-detail.effect.update-success:Property updated.`,
                ToastSeverity.Success
              );
              return landlordPropertyDetailActions.onUpdatePropertyDetail({
                status: 'success',
                message: null,
                property: res.data
              });
            }
            return landlordPropertyDetailActions.onUpdatePropertyDetail({
              status: 'error',
              message: res.niceErrorMessage,
              property: null
            });
          })
        )
      )
    )
  );

  loadPreview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(landlordPropertyDetailActions.previewLoad),
      mergeMap((action) =>
        this.propertyDetailService.getPropertyPreview(action.id).pipe(
          map((res) => {
            if (res.success) {
              return landlordPropertyDetailActions.previewLoaded({
                status: 'success',
                message: '',
                preview: res.data
              });
            } else {
              return landlordPropertyDetailActions.previewLoaded({
                status: 'error',
                message: res.niceErrorMessage,
                preview: null
              });
            }
          })
        )
      )
    )
  );

  updateStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(landlordPropertyDetailActions.updateStatus),
      switchMap(({ id, status }) =>
        this.propertyDetailService.updateStatus(id, status).pipe(
          map((res) => this.toPropertyMutation(res))
        )
      )
    )
  );

  generateDescription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(landlordPropertyDetailActions.generateDescription),
      switchMap(({ id }) =>
        this.propertyDetailService.generateDescription(id).pipe(
          map((res) => {
            if (res.success) {
              return landlordPropertyDetailActions.onGenerateDescription({
                status: 'success',
                message: null,
                errors: [],
                generated: res.data
              });
            }
            const errors: string[] = res.error?.error?.errors ?? (res.niceErrorMessage ? [res.niceErrorMessage] : []);
            return landlordPropertyDetailActions.onGenerateDescription({
              status: 'error',
              message: res.niceErrorMessage,
              errors,
              generated: null
            });
          })
        )
      )
    )
  );

  addEpcRating$ = createEffect(() =>
    this.actions$.pipe(
      ofType(landlordPropertyDetailActions.addEpcRating),
      switchMap(({ id, certificateNumber, certificate }) =>
        this.propertyDetailService.addEpcRating(id, { certificateNumber, certificate }).pipe(
          map((res) => this.toPropertyMutation(res))
        )
      )
    )
  );

  addCouncilTax$ = createEffect(() =>
    this.actions$.pipe(
      ofType(landlordPropertyDetailActions.addCouncilTax),
      switchMap(({ id, yearlyRate, document }) =>
        this.propertyDetailService.addCouncilTax(id, { yearlyRate, document }).pipe(
          map((res) => this.toPropertyMutation(res))
        )
      )
    )
  );

  addOwnershipVerification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(landlordPropertyDetailActions.addOwnershipVerification),
      switchMap(({ id, titleNumber, document }) =>
        this.propertyDetailService.addOwnershipVerification(id, { titleNumber, document }).pipe(
          map((res) => this.toPropertyMutation(res))
        )
      )
    )
  );

  dispatchVerification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(landlordPropertyDetailActions.dispatchVerification),
      switchMap(({ id, kind }) =>
        this.propertyDetailService.dispatchVerification(id, kind).pipe(
          map((res) => {
            if (res.success) {
              this.messageService.showSnackbar(res.data.message, ToastSeverity.Success);
              return landlordPropertyDetailActions.detailsLoad({ id });
            }
            this.messageService.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
            return landlordPropertyDetailActions.onPropertyMutation({
              status: 'error',
              message: res.niceErrorMessage,
              property: null
            });
          })
        )
      )
    )
  );

  loadAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(landlordPropertyDetailActions.loadAccess),
      switchMap(({ id }) =>
        this.propertyDetailService.getAccess(id).pipe(
          map((res) =>
            landlordPropertyDetailActions.accessLoaded({
              access: res.success ? res.data : null,
              error: res.success ? null : res.niceErrorMessage ?? $localize`:@@property.landlord-detail.access.load-error:We couldn’t load the access details.`
            })
          )
        )
      )
    )
  );

  updateAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(landlordPropertyDetailActions.updateAccess),
      switchMap(({ id, data }) =>
        this.propertyDetailService.patchAccess(id, data).pipe(
          map((res) => {
            if (res.success) {
              this.messageService.showSnackbar(
                $localize`:@@property.landlord-detail.effect.update-success:Property updated.`,
                ToastSeverity.Success
              );
              return landlordPropertyDetailActions.onUpdateAccess({
                status: 'success',
                message: null,
                access: res.data
              });
            }
            return landlordPropertyDetailActions.onUpdateAccess({
              status: 'error',
              message: res.niceErrorMessage,
              access: null
            });
          })
        )
      )
    )
  );

  requestKeyCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(landlordPropertyDetailActions.requestKeyCollection),
      switchMap(({ id }) =>
        this.propertyDetailService.requestKeyCollection(id).pipe(
          map((res) => {
            if (res.success) {
              this.messageService.showSnackbar(
                $localize`:@@property.landlord-detail.access.key.request-collection.success:We've requested your key back. We'll let you know when it's ready to collect.`,
                ToastSeverity.Success
              );
              return landlordPropertyDetailActions.loadAccess({ id });
            }
            this.messageService.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
            return landlordPropertyDetailActions.onPropertyMutation({
              status: 'error',
              message: res.niceErrorMessage,
              property: null
            });
          })
        )
      )
    )
  );

  resendDelegateLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(landlordPropertyDetailActions.resendDelegateLink),
      switchMap(({ id }) =>
        this.propertyDetailService.resendDelegateLink(id).pipe(
          map((res) => {
            this.messageService.showSnackbar(
              res.success
                ? $localize`:@@property.landlord-detail.access.delegate-link.resend.success:We've sent them a fresh link by email and text.`
                : (res.niceErrorMessage ??
                    $localize`:@@property.landlord-detail.access.delegate-link.resend.error:We couldn’t resend the link. Try again.`),
              res.success ? ToastSeverity.Success : ToastSeverity.Error
            );
            return landlordPropertyDetailActions.onResendDelegateLink();
          })
        )
      )
    )
  );

  cancelKeyCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(landlordPropertyDetailActions.cancelKeyCollection),
      switchMap(({ id }) =>
        this.propertyDetailService.cancelKeyCollection(id).pipe(
          map((res) => {
            if (res.success) {
              this.messageService.showSnackbar(
                $localize`:@@property.landlord-detail.access.key.cancel-collection.success:Key collection request cancelled.`,
                ToastSeverity.Success
              );
              return landlordPropertyDetailActions.loadAccess({ id });
            }
            this.messageService.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
            return landlordPropertyDetailActions.onPropertyMutation({
              status: 'error',
              message: res.niceErrorMessage,
              property: null
            });
          })
        )
      )
    )
  );

  publishProperty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(landlordPropertyDetailActions.publishProperty),
      switchMap(({ id }) =>
        this.propertyDetailService.publishProperty(id).pipe(
          map((res) => {
            if (res.success) {
              this.messageService.showSnackbar(
                $localize`:@@property.landlord-detail.effect.publish-success:Property published. Your listing is now live.`,
                ToastSeverity.Success
              );
              return landlordPropertyDetailActions.onPublishProperty({
                status: 'success',
                message: null,
                property: res.data
              });
            }
            this.messageService.showSnackbar(
              res.niceErrorMessage,
              ToastSeverity.Error
            );
            return landlordPropertyDetailActions.onPublishProperty({
              status: 'error',
              message: res.niceErrorMessage,
              property: null
            });
          })
        )
      )
    )
  );

}
